import type { StatusVariant } from '@/components/ui/StatusBadge';
import type { Job } from '@/api/types';

export const TERMINAL_JOB_STATUSES = new Set([
  'completed',
  'partial_failed',
  'failed',
  'cancelled',
]);

/** Job-level statuses that still represent in-flight work (matches backend enum). */
export const ACTIVE_JOB_STATUSES = new Set(['queued', 'processing']);

/** Item/worker statuses treated as active when inferring from row data. */
export const ACTIVE_JOB_ITEM_STATUSES = new Set([
  'queued',
  'processing',
  'pending',
  'running',
  'retrying',
]);

export function isTerminalJobStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return TERMINAL_JOB_STATUSES.has(status.toLowerCase());
}

export function isActiveJobStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return ACTIVE_JOB_STATUSES.has(status.toLowerCase());
}

export function summaryHasActiveJobs(summary: { jobs: { queued: number; processing: number } } | null): boolean {
  if (!summary) return false;
  return (summary.jobs.queued ?? 0) + (summary.jobs.processing ?? 0) > 0;
}

export function jobsListHasActiveJobs(jobs: { status: string }[]): boolean {
  return jobs.some((job) => isActiveJobStatus(job.status));
}

type JobsSummaryPollInput = {
  jobs: {
    queued: number;
    processing: number;
    completed?: number;
    failed?: number;
    partial_failed?: number;
    cancelled?: number;
  };
  queues?: {
    scrape_wake_depth?: number;
    send_wake_depth?: number;
  };
} | null;

export function summaryTotalJobs(summary: JobsSummaryPollInput): number {
  if (!summary?.jobs) return 0;
  const j = summary.jobs;
  return (
    (j.queued ?? 0) +
    (j.processing ?? 0) +
    (j.completed ?? 0) +
    (j.failed ?? 0) +
    (j.partial_failed ?? 0) +
    (j.cancelled ?? 0)
  );
}

export function summaryHasQueueActivity(summary: JobsSummaryPollInput): boolean {
  if (!summary?.queues) return false;
  return (summary.queues.scrape_wake_depth ?? 0) > 0 || (summary.queues.send_wake_depth ?? 0) > 0;
}

export function shouldPollJobsFast(
  summary: JobsSummaryPollInput,
  jobs: { status: string }[]
): boolean {
  return (
    summaryHasActiveJobs(summary) ||
    jobsListHasActiveJobs(jobs) ||
    summaryHasQueueActivity(summary)
  );
}

function jobRowSignature(job: Job): string {
  return [
    job.status,
    job.processed_items,
    job.failed_items,
    job.total_items,
    job.progress_percent,
    job.started_at ?? '',
    job.finished_at ?? '',
  ].join('|');
}

function jobIdSequence(jobs: Job[]): string {
  return jobs.map((job) => job.id).join('\u0000');
}

/** Preserve row object identity when poll data is unchanged to reduce table flicker. */
export function mergeJobRows(prev: Job[], next: Job[]): Job[] {
  if (prev.length === 0) return next;
  const nextIds = new Set(next.map((job) => job.id));
  if (prev.some((job) => !nextIds.has(job.id))) return next;
  if (prev.length !== next.length) return next;
  if (jobIdSequence(prev) !== jobIdSequence(next)) return next;
  const nextById = new Map(next.map((job) => [job.id, job]));
  return prev.map((job) => {
    const updated = nextById.get(job.id)!;
    return jobRowSignature(job) === jobRowSignature(updated) ? job : updated;
  });
}

export function jobStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    queued: 'Queued',
    processing: 'Processing',
    completed: 'Completed',
    partial_failed: 'Partially failed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    dead_letter: 'Dead letter',
  };
  return labels[status.toLowerCase()] ?? status.replace(/_/g, ' ');
}

export function jobItemStatusLabel(status: string): string {
  return jobStatusLabel(status);
}

export function jobStatusVariant(status: string): StatusVariant {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'processing':
    case 'queued':
      return 'info';
    case 'partial_failed':
      return 'warning';
    case 'failed':
    case 'cancelled':
    case 'dead_letter':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function canCancelJob(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'queued' || s === 'processing';
}

export function canRetryJob(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'failed' || s === 'partial_failed' || s === 'cancelled';
}

/** Terminal jobs that operators may remove from the Jobs dashboard. */
export function canRemoveJob(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'completed' || s === 'failed' || s === 'cancelled' || s === 'partial_failed';
}

export function canCancelJobItem(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'queued' || s === 'failed' || s === 'dead_letter';
}

export function canRetryJobItem(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'failed' || s === 'dead_letter';
}

export function formatJobType(jobType: string): string {
  const labels: Record<string, string> = {
    scrape_scheduled: 'Scheduled scrape',
    scrape_search: 'Manual search scrape',
    scrape_subscription: 'Subscription scrape',
    send_batch: 'Notification send batch',
  };
  return labels[jobType] ?? jobType.replace(/_/g, ' ');
}

export function formatJobItemType(itemType: string): string {
  const labels: Record<string, string> = {
    scrape_source: 'Scrape source',
    send_link: 'Send link',
  };
  return labels[itemType] ?? itemType.replace(/_/g, ' ');
}

export function shortId(id: string, length = 8): string {
  if (!id) return '—';
  if (id.length <= length) return id;
  return `${id.slice(0, length)}…`;
}

export function formatJobItemSource(item: {
  payload?: Record<string, unknown>;
  item_key?: string | null;
  type?: string;
}): string {
  const payload = item.payload ?? {};
  const sourceName = payload.source_name;
  if (typeof sourceName === 'string' && sourceName.trim()) {
    return sourceName;
  }
  const itemKey = item.item_key;
  if (itemKey && itemKey.startsWith('scrape:')) {
    const parts = itemKey.split(':');
    if (parts.length >= 3) {
      return `Source ${shortId(parts[parts.length - 1])}`;
    }
  }
  return item.type || 'Item';
}
